/**
 * Split contábil E-ambulantes — fonte única (OS05-C1-b).
 *
 * Comissão de produto (regra definitiva):
 * - 10% para QUALQUER vendedor/fornecedor (não há taxa de atacado).
 * - vendorNet base = subtotal - platformFee
 *
 * Frete por deliveryModality (ausência = legacy_unknown; nunca chutar):
 * - vendor_self    → 100% frete ao vendedor
 * - platform_moto  → 90% motoboy / 10% plataforma
 * - pickup         → frete tratado como 0
 * - legacy_unknown → sem split de frete (fora de recebíveis)
 *
 * Não dispara entrega/Lalamove. needsDriver é ortogonal.
 */

export type DeliveryModality =
  | 'vendor_self'
  | 'platform_moto'
  | 'pickup'
  | 'legacy_unknown';

export interface SplitResult {
  platformFee: number;
  platformRate: number;
  vendorNet: number;
  driverEarnings: number;
  deliveryPlatformFee: number;
  /** Frete creditado ao vendedor (só vendor_self). */
  vendorDeliveryFee: number;
  deliveryModality: DeliveryModality;
  /** Frete não entra em recebíveis (legacy_unknown). */
  freightExcludedFromReceivables: boolean;
}

export type OrderForSplit = {
  subtotal?: number;
  deliveryFee?: number;
  items?: Array<{ category?: string }>;
  supplierId?: string;
  deliveryModality?: DeliveryModality | string | null;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

export function resolveDeliveryModality(
  raw: OrderForSplit['deliveryModality']
): DeliveryModality {
  if (raw === 'vendor_self' || raw === 'platform_moto' || raw === 'pickup' || raw === 'legacy_unknown') {
    return raw;
  }
  // Ausência ou valor inválido → legacy_unknown (nunca chutar)
  return 'legacy_unknown';
}

export function calculateSplit(order: OrderForSplit): SplitResult {
  // Regra definitiva: 10% para QUALQUER vendedor/fornecedor. Sem exceção de atacado.
  const platformRate = 0.1;
  const subtotal = Number(order.subtotal) || 0;
  const modality = resolveDeliveryModality(order.deliveryModality);

  let deliveryFee = Number(order.deliveryFee) || 0;
  if (modality === 'pickup') deliveryFee = 0;

  const platformFee = round2(subtotal * platformRate);

  let deliveryPlatformFee = 0;
  let driverEarnings = 0;
  let vendorDeliveryFee = 0;
  let freightExcludedFromReceivables = false;

  switch (modality) {
    case 'vendor_self':
      vendorDeliveryFee = round2(deliveryFee);
      break;
    case 'platform_moto':
      deliveryPlatformFee = round2(deliveryFee * 0.1);
      driverEarnings = round2(deliveryFee - deliveryPlatformFee);
      break;
    case 'pickup':
      break;
    case 'legacy_unknown':
      freightExcludedFromReceivables = true;
      break;
  }

  const vendorNet = round2(subtotal - platformFee + vendorDeliveryFee);

  return {
    platformFee,
    platformRate,
    vendorNet,
    driverEarnings,
    deliveryPlatformFee,
    vendorDeliveryFee,
    deliveryModality: modality,
    freightExcludedFromReceivables,
  };
}

export function formatSplit(split: SplitResult) {
  const fmt = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  return {
    platformFee: fmt(split.platformFee),
    vendorNet: fmt(split.vendorNet),
    driverEarnings: fmt(split.driverEarnings),
    deliveryPlatformFee: fmt(split.deliveryPlatformFee),
    vendorDeliveryFee: fmt(split.vendorDeliveryFee),
    rateLabel: `${split.platformRate * 100}%`,
    deliveryModality: split.deliveryModality,
  };
}
